import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Avatar,
    Button,
    Grid,
    useTheme,
    Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import { userProfile, fetchUserSpecificData } from '../../features/auth/authAction';
import maleImg from '../../assets/male_modern.png';
import femaleImg from '../../assets/female_modern.png';
import moment from 'moment';

const UserProfile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    const { loggedUserInfo } = useSelector((state) => state.auth);
    const userSpecificDetails = useSelector((state) => state.user?.userSpecificData);

    // Use loggedUserInfo to get the ID, then fetch specific details
    const loggedUId = loggedUserInfo?.user_id || localStorage.getItem('user_id');

    useEffect(() => {
        if (loggedUId) {
            // Ensure we have the profile info
            dispatch(userProfile());
            // Fetch the detailed form data for this user
            dispatch(fetchUserSpecificData(loggedUId));
        }
    }, [dispatch, loggedUId]);

    if (!loggedUId) {
        return (
            <Container sx={{ mt: 10, textAlign: 'center' }}>
                <Typography variant="h5">Please log in to view your profile.</Typography>
                <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
                    Login
                </Button>
            </Container>
        );
    }

    // userSpecificDetails.userData is an array based on the slice structure seen in MoreDetailsPage
    const userData = userSpecificDetails?.userData?.[0];

    if (!userData) {
        return (
            <Container sx={{ mt: 15, mb: 10, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                    You haven't created your profile details yet.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Create your profile to start finding matches.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/service/${loggedUId}`)}
                >
                    Create Profile
                </Button>
            </Container>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            pt: 12,
            pb: 8,
            backgroundColor: theme.palette.background.default
        }}>
            <Container maxWidth="md">
                <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
                    My Profile
                </Typography>

                <Card sx={{ borderRadius: 4, mb: 4, boxShadow: theme.shadows[3] }}>
                    <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                <Avatar
                                    src={userData.gender?.toLowerCase() === 'female' ? femaleImg : maleImg}
                                    alt={userData.firstName}
                                    sx={{
                                        width: 150,
                                        height: 150,
                                        margin: '0 auto',
                                        mb: 2,

                                        border: `4px solid ${theme.palette.primary.main}`
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => navigate(`/service/${loggedUId}`)}
                                    sx={{
                                        borderRadius: 20,
                                        '&:hover': {
                                            color: 'common.white',  // White text on hover
                                            backgroundColor: 'primary.main',  // Solid primary background on hover
                                            borderColor: 'primary.main'  // Match border
                                        }
                                    }}
                                >
                                    Edit Profile
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<LockResetIcon />}
                                    onClick={() => navigate('/change-password')}
                                    sx={{
                                        mt: 2,
                                        borderRadius: 20,
                                        '&:hover': {
                                            color: 'common.white',
                                            backgroundColor: 'secondary.main',
                                            borderColor: 'secondary.main'
                                        }
                                    }}
                                >
                                    Change Password
                                </Button>


                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography variant="h5" gutterBottom>
                                    {userData.firstName} {userData.lastName}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Member since {moment(userData.createdAt).format('MMMM YYYY')}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                                        <Typography variant="body1">{userData.age}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                                        <Typography variant="body1">{userData.gender}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Religion</Typography>
                                        <Typography variant="body1">{userData.religion}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Profession</Typography>
                                        <Typography variant="body1">{userData.profession}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">City</Typography>
                                        <Typography variant="body1">{userData.city}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                                        <Typography variant="body1">{userData.country}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => navigate(`/moreDetailsPage/${loggedUId}/${loggedUId}`)}
                        sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
                    >
                        View Full Public Profile
                    </Button>
                </Box>

            </Container>
        </Box>
    );
};

export default UserProfile;
